import React from 'react';
import { X } from 'lucide-react';

/**
 * FilterChips Component
 * Displays active filters as removable chips
 */
const FilterChips = ({ 
  selectedFilters, 
  mainGroups, 
  subGroups,
  manufacturers = [],
  onRemoveFilter, 
  onClearAll 
}) => {
  const chips = [];

  // Add main group chips
  if (selectedFilters.mainGroups?.length > 0) {
    selectedFilters.mainGroups.forEach(code => {
      const group = mainGroups.find(g => g.code === code);
      chips.push({
        type: 'mainGroup',
        code,
        label: group ? `${code} - ${group.name}` : code,
        color: 'purple'
      });
    });
  }

  // Add subgroup chips
  if (selectedFilters.subGroups?.length > 0) {
    selectedFilters.subGroups.forEach(code => {
      const subgroup = subGroups.find(s => s.subgroup_code === code);
      chips.push({
        type: 'subGroup',
        code,
        label: subgroup ? `${code} - ${subgroup.name}` : code,
        color: 'orange'
      });
    });
  }

  // Add status chip
  if (selectedFilters.status) {
    chips.push({
      type: 'status',
      code: selectedFilters.status,
      label: selectedFilters.status === 'active' ? 'Active' : 'Archived',
      color: selectedFilters.status === 'active' ? 'green' : 'red'
    });
  }

  // Add manufacturer chips
  if (selectedFilters.manufacturers?.length > 0) {
    selectedFilters.manufacturers.forEach(name => {
      chips.push({
        type: 'manufacturer',
        code: name,
        label: name,
        color: 'blue'
      });
    });
  }

  if (chips.length === 0) return null;

  const colorClasses = {
    purple: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
    green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
    red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200'
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-2" data-testid="filter-chips">
      <span className="text-sm text-slate-500 font-medium">Active Filters:</span>
      
      {chips.map((chip, index) => (
        <span
          key={`${chip.type}-${chip.code}-${index}`}
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${colorClasses[chip.color]}`}
        >
          <span className="max-w-[150px] truncate">{chip.label}</span>
          <button
            onClick={() => onRemoveFilter(chip.type, chip.code)}
            className="ml-1.5 p-0.5 rounded-full hover:bg-black/10 transition-colors"
            data-testid={`remove-filter-${chip.type}-${chip.code}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}

      {chips.length > 0 && (
        <button
          onClick={onClearAll}
          className="text-xs text-slate-500 hover:text-slate-700 underline ml-2"
          data-testid="clear-all-chips"
        >
          Clear All
        </button>
      )}
    </div>
  );
};

export default FilterChips;
